/**
 * Общие presentational-части справочника пестицидов (AK-385).
 *
 * Только UI-семантика (Earth Tech DS-примитивы), без бизнес-логики:
 *   - StatusBadge — статус препарата (активен/истёк/…);
 *   - SubstanceList — состав д.в. (строка);
 *   - fmtRate — норма применения из min/max/unit.
 */
import { Badge } from '@/components/ui/badge';
import type { SubstanceContent } from '@/lib/pesticides/types';

/** Русские подписи статусов препарата (backend отдаёт коды). */
const STATUS_LABEL: Record<string, string> = {
  active: 'Активен',
  expired: 'Истёк',
  cancelled: 'Отменён',
  suspended: 'Приостановлен',
};

export function statusLabel(status: string): string {
  return STATUS_LABEL[status] ?? status;
}

export function StatusBadge({ status }: { status: string }) {
  // active → primary (зелёный), всё остальное → нейтральный secondary.
  const variant = status === 'active' ? 'primary' : 'secondary';
  return <Badge variant={variant}>{statusLabel(status)}</Badge>;
}

/** Норма применения: «0,3–0,35» / «0,3» / «—». RU-разделитель + диапазон. */
export function fmtRate(
  min: number | null,
  max: number | null,
  unit?: string | null,
): string {
  const ruNum = (n: number) => String(n).replace('.', ',');
  let value: string;
  if (min != null && max != null && min !== max) value = `${ruNum(min)}–${ruNum(max)}`;
  else if (min != null) value = ruNum(min);
  else if (max != null) value = ruNum(max);
  else return '—';
  return unit ? `${value} ${unit}` : value;
}

/** Состав д.в. одной строкой: «207 + 66,5 г/л (Клотианидин + Пенфлуфен)». */
export function substancesLine(substances: SubstanceContent[]): string {
  if (!substances.length) return '—';
  const contents = substances
    .map((s) => s.content_raw ?? (s.content_value != null ? String(s.content_value) : null))
    .filter(Boolean);
  const names = substances.map((s) => s.name_ru).join(' + ');
  const contentPart = contents.length ? contents.join(' + ') : '';
  return contentPart ? `${contentPart} (${names})` : names;
}

export function SubstanceList({ substances }: { substances: SubstanceContent[] }) {
  return (
    <span className="text-sm text-muted-foreground">
      {substancesLine(substances)}
    </span>
  );
}
